import {
  LABEL_BARCODE_HEIGHT_DOTS,
  LABEL_META_MAX_WIDTH_MM,
  LABEL_PANEL_INSET_MM,
  LABEL_TOP_PAD_MM,
} from './inventoryLabelBarcode';
import {
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_INFO_PAD_X_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_PRINT_GAP_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_PRINT_WIDTH_MM,
  mmToDots,
} from './inventoryLabelGeometry';

export interface InventoryLabelEplRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize?: string | null;
  workshopCode?: string | null;
}

function eplText(value: string, max = 40): string {
  return value
    .replace(/[\r\n"]/g, ' ')
    .trim()
    .slice(0, max);
}

function formatPriceBs(price: number): string {
  const n = Number(price);
  const s = n.toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `Bs. ${s}`;
}

function formatWeight(weightGrams: number | null): string {
  if (weightGrams == null || Number.isNaN(Number(weightGrams))) return '';
  return Number(weightGrams).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Placeholder when a label has no material text; an empty panel reads as a printing fault. */
const EPL_MATERIAL_FALLBACK = '-';

/**
 * EPL draws with the printer's built-in fonts (cell width × height in dots):
 * font 3 (12×20) for material + price, font 2 (10×16) for talla, proveedor, código and peso.
 * Same layout as the browser path; EPL has no alignment, so right edges are computed.
 */
export const EPL_INFO_FONT = 3;
export const EPL_INFO_GLYPH_DOTS = 12;
const EPL_INFO_CELL_HEIGHT_DOTS = 20;
export const EPL_SMALL_FONT = 2;
export const EPL_SMALL_GLYPH_DOTS = 10;
const EPL_SMALL_CELL_HEIGHT_DOTS = 16;
export const EPL_MATERIAL_Y = 4;
export const EPL_PRICE_Y = 28;
/** Talla shares the price's bottom edge. */
export const EPL_TALLA_Y = EPL_PRICE_Y + EPL_INFO_CELL_HEIGHT_DOTS - EPL_SMALL_CELL_HEIGHT_DOTS;
export const EPL_WORKSHOP_Y = 56;
export const EPL_META_Y = mmToDots(LABEL_TOP_PAD_MM);
/** Two dots under the código/peso cell. */
export const EPL_BARCODE_Y = EPL_META_Y + EPL_SMALL_CELL_HEIGHT_DOTS + 2;
const EPL_BARCODE_NARROW_DOTS = 1;
const EPL_BARCODE_WIDE_DOTS = 2;

/** Right edge of the info panel text, inside its padding. */
export const EPL_INFO_RIGHT_X = mmToDots(
  LABEL_INFO_LEFT_MM + LABEL_INFO_WIDTH_MM - LABEL_INFO_PAD_X_MM,
);
export const EPL_BAR_X = mmToDots(LABEL_BARCODE_LEFT_MM + LABEL_PANEL_INSET_MM);
/** Peso ends at the safe edge, clear of the strip end even when the print lands off. */
export const EPL_META_RIGHT_X = EPL_BAR_X + mmToDots(LABEL_META_MAX_WIDTH_MM);

/** EPL: empty 44 mm neck + material/price/talla/proveedor + código/peso over the bars. */
export function buildSingleLabelEpl(row: InventoryLabelEplRow): string {
  const labelWidthDots = mmToDots(LABEL_PRINT_WIDTH_MM);
  const labelHeightDots = mmToDots(LABEL_PRINT_HEIGHT_MM);
  const labelGapDots = mmToDots(LABEL_PRINT_GAP_MM);
  const infoX = mmToDots(LABEL_INFO_LEFT_MM);

  const material = eplText(row.materialLabel ?? '');
  const price = eplText(formatPriceBs(row.salePrice));
  const weight = eplText(formatWeight(row.weightGrams));
  const code = eplText(row.productCodeUnique ?? '');
  const talla = eplText(row.diameterSize ?? '', 12);
  // EPL cannot clip, so truncate to the panel instead of printing into the fold.
  const workshop = eplText(row.workshopCode ?? '').slice(
    0,
    Math.floor((EPL_INFO_RIGHT_X - infoX) / EPL_SMALL_GLYPH_DOTS),
  );

  const lines = ['N', `q${labelWidthDots}`, `Q${labelHeightDots},${labelGapDots}`];
  lines.push(
    `A${infoX},${EPL_MATERIAL_Y},0,${EPL_INFO_FONT},1,1,N,"${material === '' ? EPL_MATERIAL_FALLBACK : material}"`,
  );
  if (price !== '') lines.push(`A${infoX},${EPL_PRICE_Y},0,${EPL_INFO_FONT},1,1,N,"${price}"`);
  if (talla !== '') {
    const tallaX = EPL_INFO_RIGHT_X - talla.length * EPL_SMALL_GLYPH_DOTS;
    lines.push(`A${tallaX},${EPL_TALLA_Y},0,${EPL_SMALL_FONT},1,1,N,"${talla}"`);
  }
  if (workshop !== '') {
    lines.push(`A${infoX},${EPL_WORKSHOP_Y},0,${EPL_SMALL_FONT},1,1,N,"${workshop}"`);
  }
  if (code !== '') {
    lines.push(`A${EPL_BAR_X},${EPL_META_Y},0,${EPL_SMALL_FONT},1,1,N,"${code}"`);
    lines.push(
      `B${EPL_BAR_X},${EPL_BARCODE_Y},0,1A,${EPL_BARCODE_NARROW_DOTS},${EPL_BARCODE_WIDE_DOTS},${LABEL_BARCODE_HEIGHT_DOTS},N,"${code}"`,
    );
  }
  if (weight !== '') {
    // A line too long for the safe width pushes peso right rather than over the código.
    const codeEndX = EPL_BAR_X + (code.length + 1) * EPL_SMALL_GLYPH_DOTS;
    const weightX = Math.max(EPL_META_RIGHT_X - weight.length * EPL_SMALL_GLYPH_DOTS, codeEndX);
    lines.push(`A${weightX},${EPL_META_Y},0,${EPL_SMALL_FONT},1,1,N,"${weight}"`);
  }
  lines.push('P1', '');

  return lines.join('\n');
}

export function buildInventoryLabelsEpl(rows: InventoryLabelEplRow[]): string {
  return rows.map(buildSingleLabelEpl).join('');
}
